import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Button,
  Badge,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Spinner,
  Progress,
  Table
} from 'reactstrap';
import Swal from 'sweetalert2';
import Header from 'components/Headers/Header';
import {
  fetchFullLengthExams,
  fetchExamAttempts,
  startExamAttempt
} from '../../../redux/QuestionBank/FullLengthExamAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchQuestions } from '../../../redux/QuestionBank/QuestionsAction';
import { hasActiveAccess } from '../../../config/subscriptionPlans';
import { getDisplayName } from 'util/specialtyDisplayHelper';
import TrialBanner from '../../../components/Trial/TrialBanner';
import BoardsCountdownBanner from '../../../components/Trial/BoardsCountdownBanner';
import AddonCrossSellBanner from '../../../components/Billing/AddonCrossSellBanner';

const FullLengthExams = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { exams, examsLoading, attempts, attemptsLoading, attemptLoading } = useSelector(
    state => state.fullLengthExams || { exams: [], attempts: [], examsLoading: false }
  );
  const userDetails = useSelector(state => state.loginAuth?.userDetails);
  const { subjects } = useSelector(state => state.questionBankSubjects || { subjects: [] });
  const { units } = useSelector(state => state.questionBankUnits || { units: [] });
  const { topics } = useSelector(state => state.questionBankTopics || { topics: [] });
  const { questions } = useSelector(state => state.questionBankQuestions || { questions: [] });
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);

  const [selectedExam, setSelectedExam] = useState(null);
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [coverageExam, setCoverageExam] = useState(null);

  // Pagination state for attempt history
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchFullLengthExams({ activeOnly: true }));
    if (userDetails?.handle) {
      dispatch(fetchExamAttempts());
    }
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchQuestions({ pageSize: 5000 }));
  }, [dispatch, userDetails?.handle, activeSpecialty]);

  const getExamDerivedFilters = useMemo(() => {
    return (exam) => {
      // New-style exams with embedded questions
      if (exam.questions && exam.questions.length > 0) {
        return {
          subjects: [...new Set(exam.questions.map(q => q.subjectName).filter(Boolean))],
          units: [...new Set(exam.questions.map(q => q.unitName).filter(Boolean))],
          topics: [...new Set(exam.questions.map(q => q.topicName).filter(Boolean))],
          isEmbedded: true
        };
      }
      if (exam.subjects?.length > 0) {
        return {
          subjects: exam.subjects,
          units: exam.units || [],
          topics: exam.topics || []
        };
      }
      if (!exam.questionIds?.length || !questions?.length) return { subjects: [], units: [], topics: [] };
      const examQuestions = questions.filter(q => exam.questionIds.includes(q.questionId));
      return {
        subjects: [...new Set(examQuestions.map(q => q.subjectId).filter(Boolean))],
        units: [...new Set(examQuestions.map(q => q.unitId).filter(Boolean))],
        topics: [...new Set(examQuestions.map(q => q.topicId).filter(Boolean))]
      };
    };
  }, [questions]);

  // Active question IDs set (questions already excludes archived via fetchQuestions)
  const activeQuestionIds = useMemo(() => {
    return new Set((questions || []).map(q => q.questionId));
  }, [questions]);

  // Get active question count for an exam (excludes archived)
  const getActiveQuestionCount = (exam) => {
    if (exam.questions) return exam.questions.length; // New-style embedded questions
    const ids = exam.questionIds || [];
    if (!ids.length || !activeQuestionIds.size) return exam.questionCount || ids.length || 0;
    return ids.filter(id => activeQuestionIds.has(id)).length;
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects?.find(s => s.subjectId === subjectId);
    if (!subject) return subjectId;
    return getDisplayName(subject, activeSpecialty);
  };

  const getSubjectFull = (subjectId) => {
    const subject = subjects?.find(s => s.subjectId === subjectId);
    return subject || null;
  };

  const getUnitFull = (unitId) => {
    const unit = units?.find(u => u.unitId === unitId);
    return unit || null;
  };

  const getTopicFull = (topicId) => {
    const topic = topics?.find(t => t.topicId === topicId);
    return topic || null;
  };

  // Build coverage tree: Subject > Unit > Topic
  const getCoverageTree = useMemo(() => {
    return (exam) => {
      const filters = getExamDerivedFilters(exam);

      // Embedded exams store names directly, not IDs
      if (filters.isEmbedded && exam.questions) {
        const subjectMap = {};
        exam.questions.forEach(q => {
          const subName = q.subjectName || 'Unknown';
          if (!subjectMap[subName]) {
            subjectMap[subName] = { id: subName, name: subName, code: '', unitMap: {} };
          }
          if (q.unitName) {
            if (!subjectMap[subName].unitMap[q.unitName]) {
              subjectMap[subName].unitMap[q.unitName] = { id: q.unitName, name: q.unitName, code: '', topicSet: new Set() };
            }
            if (q.topicName) {
              subjectMap[subName].unitMap[q.unitName].topicSet.add(q.topicName);
            }
          }
        });
        return Object.values(subjectMap).map(s => ({
          id: s.id,
          code: s.code,
          name: s.name,
          units: Object.values(s.unitMap).map(u => ({
            id: u.id,
            code: u.code,
            name: u.name,
            topics: [...u.topicSet].map(t => ({ id: t, code: '', name: t }))
          }))
        }));
      }

      // Legacy/QBank exams use IDs — look up from Redux
      const tree = [];
      filters.subjects.forEach(subjectId => {
        const subject = getSubjectFull(subjectId);
        if (!subject) return;

        const subjectUnits = filters.units
          .map(uid => getUnitFull(uid))
          .filter(u => u && u.subjectId === subjectId);

        const subjectNode = {
          id: subjectId,
          code: subject.code,
          name: subject.name,
          units: subjectUnits.map(unit => {
            const unitTopics = filters.topics
              .map(tid => getTopicFull(tid))
              .filter(t => t && t.unitId === unit.unitId);

            return {
              id: unit.unitId,
              code: unit.code,
              name: unit.name,
              topics: unitTopics.map(topic => ({
                id: topic.topicId,
                code: topic.code,
                name: topic.name
              }))
            };
          })
        };

        tree.push(subjectNode);
      });

      return tree;
    };
  }, [subjects, units, topics, getExamDerivedFilters]);

  const formatTime = (seconds) => {
    if (!seconds) return '--';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes} min`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '--';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getExamAttempts = (examId) => attempts?.filter(a => a.examId === examId) || [];
  const getBestScore = (examId) => {
    const completed = getExamAttempts(examId).filter(a => a.status === 'completed' && a.score !== undefined);
    return completed.length > 0 ? Math.max(...completed.map(a => a.score)) : null;
  };
  const hasInProgressAttempt = (examId) => getExamAttempts(examId).some(a => a.status === 'in_progress');
  const getInProgressAttempt = (examId) => getExamAttempts(examId).find(a => a.status === 'in_progress');

  const handleStartExam = async () => {
    if (!selectedExam) return;
    const inProgressAttempt = getInProgressAttempt(selectedExam.examId);
    if (inProgressAttempt) {
      const result = await Swal.fire({
        title: 'Resume or Start New?',
        text: 'You have an exam in progress.',
        icon: 'question',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Resume',
        denyButtonText: 'Start New',
        cancelButtonText: 'Cancel'
      });
      if (result.isConfirmed) {
        Swal.fire({ title: 'Resuming Exam...', allowOutsideClick: false, allowEscapeKey: false, didOpen: () => Swal.showLoading() });
        history.push(`/client/question-bank/full-length-exam/${inProgressAttempt.attemptId}`);
        return;
      } else if (!result.isDenied) return;
    }
    setIsStartModalOpen(false);
    Swal.fire({ title: 'Preparing Exam...', allowOutsideClick: false, allowEscapeKey: false, didOpen: () => Swal.showLoading() });
    const result = await dispatch(startExamAttempt(selectedExam.examId));
    if (result.success) history.push(`/client/question-bank/full-length-exam/${result.attemptId}`);
  };

  const handleResumeExam = (attemptId) => {
    Swal.fire({ title: 'Resuming Exam...', allowOutsideClick: false, allowEscapeKey: false, didOpen: () => Swal.showLoading() });
    history.push(`/client/question-bank/full-length-exam/${attemptId}`);
  };
  const handleViewResults = (attemptId) => history.push(`/client/question-bank/full-length-exam/${attemptId}/results`);

  const completedAttempts = attempts?.filter(a => a.status === 'completed') || [];
  const inProgressAttempts = attempts?.filter(a => a.status === 'in_progress') || [];
  const avgScore = completedAttempts.length > 0
    ? Math.round(completedAttempts.reduce((sum, a) => sum + (a.score || 0), 0) / completedAttempts.length)
    : null;

  const getScoreColor = (score) => {
    if (score >= 70) return 'success';
    if (score >= 50) return 'warning';
    return 'danger';
  };

  // Sort attempts by date (newest first)
  const sortedAttempts = useMemo(() => {
    return [...(attempts || [])].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [attempts]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAttempts = sortedAttempts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedAttempts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const hasAccess = hasActiveAccess(userDetails, activeSpecialty);

  // Users without active access cannot view full-length exams
  if (!hasAccess) {
    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Row className="justify-content-center">
            <Col lg="8" md="10">
              <Card className="shadow border-0">
                <CardBody className="text-center py-5">
                  <div className="mb-4">
                    <i className="fas fa-lock" style={{ fontSize: '48px', color: '#5e72e4' }}></i>
                  </div>
                  <h2 className="mb-3">Full-Length Practice Exams</h2>
                  <p className="text-muted mb-4" style={{ fontSize: '16px', maxWidth: '500px', margin: '0 auto' }}>
                    Subscribe to access full-length practice exams that simulate real board exam conditions.
                  </p>
                  <div className="bg-light rounded p-4 mb-4 text-left" style={{ maxWidth: '400px', margin: '0 auto' }}>
                    <h6 className="font-weight-bold mb-3">What you get:</h6>
                    <ul className="mb-0 pl-3" style={{ fontSize: '14px' }}>
                      <li className="mb-2">Timed exams with real test conditions</li>
                      <li className="mb-2">Sectioned exams with scheduled breaks</li>
                      <li className="mb-2">Detailed score reports and analytics</li>
                      <li className="mb-2">Track your progress across attempts</li>
                    </ul>
                  </div>
                  <Button
                    color="success"
                    size="lg"
                    onClick={() => history.push('/client/plans')}
                  >
                    <i className="fas fa-crown mr-2"></i>
                    View Plans
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <TrialBanner />
        <BoardsCountdownBanner />
        <AddonCrossSellBanner />
        {/* Summary Stats */}
        <Row className="mb-4">
          <Col lg="3" md="6" className="mb-3 mb-lg-0">
            <Card className="shadow-sm border-0">
              <CardBody className="py-3">
                <Row className="align-items-center">
                  <Col xs="auto">
                    <div className="bg-primary text-white rounded d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                      <i className="fas fa-file-alt fa-lg"></i>
                    </div>
                  </Col>
                  <Col>
                    <h5 className="text-muted mb-0 text-sm">Available</h5>
                    <h3 className="mb-0 font-weight-bold">{exams?.length || 0}</h3>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col lg="3" md="6" className="mb-3 mb-lg-0">
            <Card className="shadow-sm border-0">
              <CardBody className="py-3">
                <Row className="align-items-center">
                  <Col xs="auto">
                    <div className="bg-success text-white rounded d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                      <i className="fas fa-check fa-lg"></i>
                    </div>
                  </Col>
                  <Col>
                    <h5 className="text-muted mb-0 text-sm">Completed</h5>
                    <h3 className="mb-0 font-weight-bold">{completedAttempts.length}</h3>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col lg="3" md="6" className="mb-3 mb-lg-0">
            <Card className="shadow-sm border-0">
              <CardBody className="py-3">
                <Row className="align-items-center">
                  <Col xs="auto">
                    <div className="bg-warning text-white rounded d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                      <i className="fas fa-clock fa-lg"></i>
                    </div>
                  </Col>
                  <Col>
                    <h5 className="text-muted mb-0 text-sm">In Progress</h5>
                    <h3 className="mb-0 font-weight-bold">{inProgressAttempts.length}</h3>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col lg="3" md="6">
            <Card className="shadow-sm border-0">
              <CardBody className="py-3">
                <Row className="align-items-center">
                  <Col xs="auto">
                    <div className="bg-info text-white rounded d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                      <i className="fas fa-chart-line fa-lg"></i>
                    </div>
                  </Col>
                  <Col>
                    <h5 className="text-muted mb-0 text-sm">Avg. Score</h5>
                    <h3 className="mb-0 font-weight-bold">{avgScore !== null ? `${avgScore}%` : '--'}</h3>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* In Progress Alert */}
        {inProgressAttempts.length > 0 && (
          <Card className="shadow-sm border-0 mb-4" style={{ borderLeft: '4px solid #fb6340' }}>
            <CardBody className="py-3">
              <Row className="align-items-center">
                <Col xs="auto">
                  <i className="fas fa-exclamation-circle text-warning fa-lg"></i>
                </Col>
                <Col>
                  <strong>You have an exam in progress:</strong> {inProgressAttempts[0].examName}
                  {inProgressAttempts[0].timeRemaining && (
                    <span className="ml-2 text-muted">({formatTime(inProgressAttempts[0].timeRemaining)} remaining)</span>
                  )}
                </Col>
                <Col xs="auto">
                  <Button color="warning" size="sm" onClick={() => handleResumeExam(inProgressAttempts[0].attemptId)}>
                    <i className="fas fa-play mr-2"></i>Resume
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {/* Practice Exams Section */}
        <Card className="shadow mb-4">
          <CardHeader className="bg-white border-0">
            <h3 className="mb-0">Practice Exams</h3>
          </CardHeader>
          <CardBody>
            {examsLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="text-muted mt-2 mb-0">Loading exams...</p>
              </div>
            ) : !exams || exams.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                <h5 className="text-muted">No Exams Available</h5>
                <p className="text-muted mb-0">Practice exams will appear here when available.</p>
              </div>
            ) : (
              <Row>
                {exams.map((exam) => {
                  const bestScore = getBestScore(exam.examId);
                  const attemptCount = getExamAttempts(exam.examId).length;
                  const inProgress = hasInProgressAttempt(exam.examId);
                  const inProgressAttempt = getInProgressAttempt(exam.examId);
                  const derivedFilters = getExamDerivedFilters(exam);

                  return (
                    <Col lg="4" md="6" key={exam.examId} className="mb-4">
                      <Card className="shadow-sm border h-100">
                        <CardBody className="d-flex flex-column">
                          {/* Header */}
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <h5 className="mb-0 font-weight-bold">{exam.name}</h5>
                            {inProgress && (
                              <Badge color="warning" className="ml-2">In Progress</Badge>
                            )}
                          </div>

                          {/* Subjects Preview */}
                          {derivedFilters.subjects?.length > 0 && (
                            <div className="mb-3">
                              {derivedFilters.subjects.slice(0, 2).map(subjectId => (
                                <Badge key={subjectId} color="light" className="mr-1 mb-1 text-muted" style={{ fontWeight: '400' }}>
                                  {getSubjectName(subjectId)}
                                </Badge>
                              ))}
                              {derivedFilters.subjects.length > 2 && (
                                <Badge color="secondary" className="mr-1 mb-1">+{derivedFilters.subjects.length - 2}</Badge>
                              )}
                              <span
                                className="text-primary small ml-1"
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => setCoverageExam(exam)}
                              >
                                View Coverage
                              </span>
                            </div>
                          )}

                          {/* Stats */}
                          <Row className="text-center mb-3">
                            <Col xs="4">
                              <h4 className="mb-0 font-weight-bold text-primary">
                                {getActiveQuestionCount(exam)}
                              </h4>
                              <small className="text-muted">Questions</small>
                            </Col>
                            <Col xs="4">
                              <h4 className="mb-0 font-weight-bold text-info">
                                {exam.hasSections
                                  ? formatTime(exam.sections?.reduce((sum, s) => sum + s.timeLimitSeconds, 0) || exam.timeLimit)
                                  : formatTime(getActiveQuestionCount(exam) * 66)
                                }
                              </h4>
                              <small className="text-muted">
                                {exam.hasSections ? `${exam.sections?.length || 0} Sections` : 'Duration'}
                              </small>
                            </Col>
                            <Col xs="4">
                              <h4 className="mb-0 font-weight-bold text-dark">
                                {attemptCount}
                              </h4>
                              <small className="text-muted">Attempts</small>
                            </Col>
                          </Row>

                          {/* Section Info */}
                          {exam.hasSections && exam.sections && (
                            <div className="mb-3 px-2 py-2 rounded" style={{ backgroundColor: '#f8f9fe', fontSize: '0.8rem' }}>
                              {exam.sections.map((s, idx) => (
                                <div key={idx} className="d-flex justify-content-between">
                                  <span className="text-muted">{s.name}</span>
                                  <span>{s.questionCount}q, {formatTime(s.timeLimitSeconds)}</span>
                                </div>
                              ))}
                              {exam.breakDurationSeconds > 0 && (
                                <div className="d-flex justify-content-between mt-1 pt-1" style={{ borderTop: '1px solid #dee2e6' }}>
                                  <span className="text-muted"><i className="fas fa-coffee mr-1"></i>Break</span>
                                  <span>{Math.round(exam.breakDurationSeconds / 60)} min</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Best Score */}
                          {bestScore !== null && (
                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <small className="text-muted">Best Score</small>
                                <strong className={`text-${getScoreColor(bestScore)}`}>{bestScore}%</strong>
                              </div>
                              <Progress
                                value={bestScore}
                                color={getScoreColor(bestScore)}
                                style={{ height: '6px' }}
                              />
                            </div>
                          )}

                          {/* Time Remaining for In Progress */}
                          {inProgress && inProgressAttempt?.timeRemaining && (
                            <div className="bg-lighter rounded p-2 mb-3 text-center">
                              <small className="text-warning font-weight-bold">
                                <i className="fas fa-clock mr-1"></i>
                                {formatTime(inProgressAttempt.timeRemaining)} remaining
                              </small>
                            </div>
                          )}

                          {/* Action Button */}
                          <div className="mt-auto">
                            {inProgress ? (
                              <Button
                                color="warning"
                                block
                                onClick={() => handleResumeExam(inProgressAttempt.attemptId)}
                              >
                                <i className="fas fa-play mr-2"></i>Resume Exam
                              </Button>
                            ) : (
                              <Button
                                color="primary"
                                block
                                onClick={() => { setSelectedExam(exam); setIsStartModalOpen(true); }}
                              >
                                <i className="fas fa-play mr-2"></i>
                                {attemptCount > 0 ? 'Retake Exam' : 'Start Exam'}
                              </Button>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </CardBody>
        </Card>

        {/* Attempt History */}
        {attempts && attempts.length > 0 && (
          <Card className="shadow mb-5">
            <CardHeader className="bg-white border-0">
              <h3 className="mb-0">
                <i className="fas fa-history mr-2"></i>
                Attempt History
              </h3>
            </CardHeader>
            <CardBody className="p-0">
              {attemptsLoading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-items-center table-flush" hover>
                    <thead className="thead-light">
                      <tr>
                        <th>Score</th>
                        <th>Exam</th>
                        <th>Date</th>
                        <th className="text-center">Attempt</th>
                        <th className="text-center">Result</th>
                        <th className="text-center">Time Used</th>
                        <th className="text-center">Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentAttempts.map((attempt) => (
                        <tr key={attempt.attemptId}>
                          <td>
                            {attempt.status === 'completed' && attempt.score != null ? (
                              <span
                                className={`font-weight-bold text-${getScoreColor(attempt.score)}`}
                                style={{ fontSize: '1rem' }}
                              >
                                {attempt.score}%
                              </span>
                            ) : attempt.status === 'in_progress' ? (
                              <Badge color="info" pill>In Progress</Badge>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>
                          <td>
                            <strong>{attempt.examName}</strong>
                          </td>
                          <td>
                            <span>{formatDate(attempt.createdAt)}</span>
                          </td>
                          <td className="text-center">
                            #{attempt.attemptNumber}
                          </td>
                          <td className="text-center">
                            {attempt.status === 'completed' ? (
                              <span className="text-muted">
                                {attempt.correctAnswers}/{attempt.totalQuestions}
                              </span>
                            ) : (
                              <span className="text-muted">--</span>
                            )}
                          </td>
                          <td className="text-center">
                            {attempt.status === 'completed' && attempt.timeLimit && attempt.timeRemaining !== undefined ? (
                              formatTime(attempt.timeLimit - attempt.timeRemaining)
                            ) : attempt.status === 'in_progress' && attempt.timeRemaining ? (
                              <span className="text-warning">
                                {formatTime(attempt.timeRemaining)} left
                              </span>
                            ) : (
                              <span className="text-muted">--</span>
                            )}
                          </td>
                          <td className="text-center">
                            <Badge color={attempt.status === 'completed' ? 'success' : 'warning'}>
                              {attempt.status === 'in_progress' ? 'In Progress' : 'Completed'}
                            </Badge>
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm" role="group">
                              {attempt.status === 'in_progress' ? (
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => handleResumeExam(attempt.attemptId)}
                                  title="Resume Exam"
                                >
                                  <i className="fas fa-play"></i>
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => handleViewResults(attempt.attemptId)}
                                    title="View Results"
                                  >
                                    <i className="fas fa-chart-bar"></i>
                                  </Button>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => history.push(
                                      `/client/question-bank/full-length-exam/${attempt.attemptId}`,
                                      { reviewMode: true }
                                    )}
                                    title="Review Questions"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
            {!attemptsLoading && sortedAttempts.length > 0 && (
              <CardFooter className="py-4">
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
                  <div className="text-muted small mb-2 mb-sm-0">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedAttempts.length)} of {sortedAttempts.length} attempts
                  </div>
                  <div className="d-flex align-items-center">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || totalPages <= 1}
                      className="mr-2"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <span className="d-none d-sm-inline ml-1">Previous</span>
                    </Button>
                    <span className="mx-2 text-nowrap">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || totalPages <= 1}
                      className="ml-2"
                    >
                      <span className="d-none d-sm-inline mr-1">Next</span>
                      <i className="fas fa-chevron-right"></i>
                    </Button>
                  </div>
                </div>
              </CardFooter>
            )}
          </Card>
        )}
      </Container>

      {/* Coverage Modal */}
      <Modal isOpen={!!coverageExam} toggle={() => setCoverageExam(null)} size="lg">
        <ModalHeader toggle={() => setCoverageExam(null)}>
          <i className="fas fa-layer-group text-primary mr-2"></i>
          Exam Coverage
        </ModalHeader>
        <ModalBody>
          {coverageExam && (() => {
            const tree = getCoverageTree(coverageExam);
            const filters = getExamDerivedFilters(coverageExam);
            return (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                  <h5 className="mb-0 font-weight-bold">{coverageExam.name}</h5>
                  <div>
                    <Badge color="primary" className="mr-1">{filters.subjects.length} Subjects</Badge>
                    <Badge color="info" className="mr-1">{filters.units.length} Units</Badge>
                    <Badge color="success">{filters.topics.length} Topics</Badge>
                  </div>
                </div>
                {tree.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-info-circle mr-1"></i>
                    No coverage data available.
                  </div>
                ) : (
                  tree.map((subject) => (
                    <div key={subject.id} className="mb-3">
                      {/* Subject */}
                      <div className="d-flex align-items-center p-2 bg-primary rounded mb-1" style={{ opacity: 0.9 }}>
                        <i className="fas fa-book text-white mr-2"></i>
                        <span className="text-white font-weight-bold" style={{ fontSize: '14px' }}>
                          {getDisplayName(subject, activeSpecialty)}
                        </span>
                        {subject.units.length > 0 && (
                          <Badge color="" className="ml-auto bg-white text-primary" style={{ fontSize: '11px' }}>
                            {subject.units.length} {subject.units.length === 1 ? 'unit' : 'units'}
                          </Badge>
                        )}
                      </div>
                      {/* Units */}
                      {subject.units.map((unit) => (
                        <div key={unit.id} className="ml-3">
                          <div className="d-flex align-items-center py-2 px-2 border-left" style={{ borderLeftWidth: '3px', borderLeftColor: '#5e72e4' }}>
                            <i className="fas fa-folder text-info mr-2" style={{ fontSize: '12px' }}></i>
                            <span style={{ fontSize: '13px' }} className="font-weight-bold">
                              {getDisplayName(unit, activeSpecialty)}
                            </span>
                            {unit.topics.length > 0 && (
                              <small className="text-muted ml-auto">{unit.topics.length} {unit.topics.length === 1 ? 'topic' : 'topics'}</small>
                            )}
                          </div>
                          {/* Topics */}
                          {unit.topics.map((topic) => (
                            <div key={topic.id} className="d-flex align-items-center py-1 px-2 ml-3 border-left" style={{ borderLeftWidth: '2px', borderLeftColor: '#2dce89' }}>
                              <i className="fas fa-angle-right text-success mr-2" style={{ fontSize: '11px' }}></i>
                              <span className="text-muted" style={{ fontSize: '12px' }}>
                                {getDisplayName(topic, activeSpecialty)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCoverageExam(null)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Start Exam Modal */}
      <Modal isOpen={isStartModalOpen} toggle={() => setIsStartModalOpen(false)} centered>
        <ModalHeader toggle={() => setIsStartModalOpen(false)}>
          Start Exam
        </ModalHeader>
        <ModalBody>
          {selectedExam && (
            <>
              <h4 className="mb-4">{selectedExam.name}</h4>

              <Row className="mb-4">
                <Col xs="6">
                  <div className="border rounded p-3 text-center">
                    <h3 className="mb-0 text-primary font-weight-bold">
                      {getActiveQuestionCount(selectedExam)}
                    </h3>
                    <small className="text-muted">Questions</small>
                  </div>
                </Col>
                <Col xs="6">
                  <div className="border rounded p-3 text-center">
                    <h3 className="mb-0 text-info font-weight-bold">
                      {selectedExam.hasSections
                        ? formatTime(selectedExam.sections?.reduce((sum, s) => sum + s.timeLimitSeconds, 0) || selectedExam.timeLimit)
                        : formatTime(getActiveQuestionCount(selectedExam) * 66)
                      }
                    </h3>
                    <small className="text-muted">
                      {selectedExam.hasSections ? 'Active Testing' : 'Time Limit'}
                    </small>
                  </div>
                </Col>
              </Row>

              {/* Section breakdown for sectioned exams */}
              {selectedExam.hasSections && selectedExam.sections && (
                <div className="border rounded p-3 mb-4" style={{ backgroundColor: '#f8f9fe' }}>
                  {selectedExam.sections.map((s, idx) => (
                    <div key={idx} className="d-flex justify-content-between mb-1">
                      <span><strong>{s.name}</strong></span>
                      <span>{s.questionCount} questions, {formatTime(s.timeLimitSeconds)}</span>
                    </div>
                  ))}
                  {selectedExam.breakDurationSeconds > 0 && (
                    <div className="d-flex justify-content-between mt-2 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                      <span><i className="fas fa-coffee mr-1"></i> Break between sections</span>
                      <span>{Math.round(selectedExam.breakDurationSeconds / 60)} min (skippable)</span>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-light rounded p-3">
                <h6 className="mb-2">Before you begin:</h6>
                <ul className="mb-0 pl-3 text-muted small">
                  <li>Timer starts immediately when you begin</li>
                  <li>Answers are hidden until you submit</li>
                  <li>Progress is auto-saved every 30 seconds</li>
                  <li>Questions are presented in random order</li>
                  {selectedExam.hasSections && (
                    <li>Once a section is completed, you cannot go back to it</li>
                  )}
                </ul>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsStartModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleStartExam} disabled={attemptLoading}>
            {attemptLoading ? (
              <><Spinner size="sm" className="mr-2" />Starting...</>
            ) : (
              <><i className="fas fa-play mr-2"></i>Begin Exam</>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default FullLengthExams;
